/**
 * SHER-1128 (SHER-308 Phase 4) — insurer-assignment grid model.
 *
 * The second Billing tab (Decision A) assigns each insurer to a price list: columns
 * InsuranceCode | Name | PriceList, the same ReactGrid look as the price tab. Name is DISPLAY
 * for an existing insurer (looked up by code; insurers are not renamed here) and EDITABLE only
 * on a new add-row that creates a named insurer (Decision B — persisted via the existing
 * addInsuranceToInvestigation on Save, no API change). The price list is always editable.
 *
 * SHER-714: a Status column shows whether each insurer is active. Its cell (statusToggleCell)
 * toggles Active ⇄ Inactive on a click or Space/Enter on an existing insurer (Deactivate /
 * Reactivate, applied on Save through the assignments CSV's Status column); an inactive insurer
 * stays listed, greyed, so it can come back.
 *
 * Pure builders so the component stays a thin ReactGrid wrapper and the shape is unit-pinned
 * (ReactGrid does not render under jsdom).
 */
import { Column, DefaultCellTypes, Row } from '@silevis/reactgrid';
import { ACTION_COLUMN_ID, HEADER_ROW_ID, ADD_ROW_FOOTER_ID } from './editableGrid';
import { parseCsvLine } from './billingCsv';
import { StatusToggleCell } from './statusToggleCell';

export type AssignmentGridCell = DefaultCellTypes | StatusToggleCell;

export const STATUS_COLUMN_ID = 'status';
export const ASSIGNMENT_COLUMN_IDS = ['insuranceCode', 'name', 'priceList', STATUS_COLUMN_ID] as const;

/** The default price list for a newly created insurer, per Decision A. */
export const DEFAULT_ASSIGNMENT_LIST = 'STD';

const ACTION_COLUMN_WIDTH = 80;
const DEFAULT_WIDTHS: Record<string, number> = {
    [ACTION_COLUMN_ID]: ACTION_COLUMN_WIDTH,
    insuranceCode: 160,
    name: 260,
    priceList: 140,
    [STATUS_COLUMN_ID]: 110,
};

export interface AssignmentRow {
    action?: '' | 'DELETE';
    insuranceCode: string;
    name: string;
    /** list code the insurer is assigned to ('' = unassigned). */
    priceList: string;
    /** true for a row added in the grid: it creates a new insurer, so code and name are editable. */
    isNew?: boolean;
    /**
     * SHER-714: the insurer's state. `undefined` only for a code from an uploaded file the grid
     * does not know: shown as Active (what onboarding creates) and sent as an empty Status, so the
     * server leaves an existing insurer as it is. A new add-row is born `true`.
     */
    active?: boolean;
}

const isInactive = (row: AssignmentRow) => row.active === false;


/** Columns: the action column plus insurer code, name and price list. */
export function buildAssignmentColumns(widths: Record<string, number> = {}): Column[] {
    const width = (id: string) => widths[id] ?? DEFAULT_WIDTHS[id] ?? 140;
    return [
        { columnId: ACTION_COLUMN_ID, width: width(ACTION_COLUMN_ID), resizable: false },
        ...ASSIGNMENT_COLUMN_IDS.map((id) => ({ columnId: id, width: width(id), resizable: true })),
    ];
}

/**
 * Rows: a sticky header, one row per insurer (code/name locked for existing insurers, editable
 * for a new add-row; price list always editable; delete rows flagged red), and the add-row
 * footer that creates a new insurer.
 */
export function buildAssignmentRows(rows: AssignmentRow[]): Row<AssignmentGridCell>[] {
    const headerRow: Row<AssignmentGridCell> = {
        rowId: HEADER_ROW_ID,
        cells: [
            { type: 'header', text: 'Action' },
            { type: 'header', text: 'Insurance Code' },
            { type: 'header', text: 'Name' },
            { type: 'header', text: 'Price List' },
            { type: 'header', text: 'Status' },
        ],
    };

    const dataRows: Row<AssignmentGridCell>[] = rows.map((row, index) => {
        const isDeleted = row.action === 'DELETE';
        const cls = [isDeleted ? 'deleted-row' : '', isInactive(row) ? 'inactive-row' : ''].filter(Boolean).join(' ');
        // Existing insurers: code and name are read-only (name is display, no rename here).
        // New add-rows: both editable so the user can name the insurer being created.
        const locked = !row.isNew;
        return {
            rowId: index,
            cells: [
                { type: 'text', text: isDeleted ? '❌' : '🗑️', nonEditable: false, className: cls },
                { type: 'text', text: row.insuranceCode, nonEditable: locked, className: cls },
                { type: 'text', text: row.name, nonEditable: locked, className: cls },
                { type: 'text', text: row.priceList, nonEditable: false, className: cls },
                // Toggled by an explicit act only, labelled by the template (statusToggleCell). The
                // lock on a new insurer (born active) is this nonEditable, honoured by the template.
                { type: 'statusToggle', active: !isInactive(row), nonEditable: !!row.isNew, className: cls },
            ],
        };
    });

    const footerRow: Row<AssignmentGridCell> = {
        rowId: ADD_ROW_FOOTER_ID,
        cells: [
            { type: 'text', text: '➕', nonEditable: false, className: 'add-row-cell' },
            { type: 'text', text: 'Click + to add a new insurer', nonEditable: true, className: 'add-row-hint' },
            { type: 'text', text: '', nonEditable: true },
            { type: 'text', text: '', nonEditable: true },
            { type: 'text', text: '', nonEditable: true },
        ],
    };

    return [headerRow, ...dataRows, footerRow];
}

const statusCell = (row: AssignmentRow): string => {
    if (row.active === undefined) return '';
    return row.active ? 'ACTIVE' : 'INACTIVE';
};

/**
 * Serialize assignment rows to an assignments.csv (`Action,InsuranceCode,PriceList,Status`), the
 * Phase-3 format plus SHER-714's Status. Rows with no code are dropped (an unfilled add-row). Name
 * is intentionally not emitted — the bulk CSV does not carry it (a named insurer is created via
 * the add-row API path). An unknown state is sent empty, which the server reads as "leave it".
 */
export function generateAssignmentsCsv(rows: AssignmentRow[]): string {
    const header = 'Action,InsuranceCode,PriceList,Status';
    const body = rows
        .filter(r => r.insuranceCode?.trim())
        .map(r => `${r.action || ''},${r.insuranceCode.trim()},${(r.priceList || '').trim()},${statusCell(r)}`);
    return [header, ...body].join('\n');
}

const parseStatus = (cell: string | undefined): boolean | undefined => {
    const value = (cell || '').trim().toUpperCase();
    if (value === 'ACTIVE') return true;
    if (value === 'INACTIVE') return false;
    return undefined;
};

/** What the grid knows about an insurer, by code, from the last fetch. */
export type KnownInsurers = Record<string, { name: string; active: boolean }>;

/**
 * Parse an assignments.csv into rows. The file carries no name, so each row's display name is
 * looked up from `known` and falls back to the code itself. SHER-714: a row without a Status (a
 * file from before it) takes the insurer's known state; the server's own export always has one.
 */
export function parseAssignmentsCsv(csvContent: string, known: KnownInsurers): AssignmentRow[] {
    const lines = csvContent.trim().split('\n');
    if (lines.length <= 1) return [];
    return lines.slice(1)
        .map(line => {
            const v = parseCsvLine(line);
            const code = v[1] || '';
            return {
                action: v[0]?.toUpperCase() === 'DELETE' ? 'DELETE' : '',
                insuranceCode: code,
                name: known[code]?.name ?? code,
                priceList: v[2] || '',
                active: parseStatus(v[3]) ?? known[code]?.active,
            } as AssignmentRow;
        })
        .filter(r => r.insuranceCode);
}
